/**
 * LearnPreviewRead.jsx — flashes just the passage's opening and closing
 * sentences for 5 seconds (the "mental map" preview), then hides them
 * and shows the full passage. Trains the habit of building a scaffold
 * before diving into a long read.
 *
 * Bonus XP kicks in automatically via the standard reward path when
 * comprehension ≥ 80% — the intended payoff is "the technique works
 * for me" made visible on the results card.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Play, StopCircle, ArrowLeft, ChevronDown, Clock } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { readCustomPassage } from '../utils/learnCustomPassage';
import LearnQuiz from '../components/LearnQuiz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PREVIEW_SECS = 5;

const firstAndLastSentence = (text) => {
  const parts = text.match(/[^.!?]+[.!?]+/g) || [text];
  if (parts.length <= 2) return parts.map((s) => s.trim());
  return [parts[0].trim(), parts[parts.length - 1].trim()];
};

const LearnPreviewRead = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passageIdx, setPassageIdx] = useState(0);
  const [state, setState] = useState('setup'); // setup|preview|reading|quiz|done
  const [previewLeft, setPreviewLeft] = useState(PREVIEW_SECS);
  const startedAt = useRef(0);
  const durationRef = useRef(0);
  const previewTimer = useRef(null);

  useEffect(() => {
    const custom = readCustomPassage();
    if (custom) { setPassages([custom]); return; }
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/learn/passages`);
        if (r.ok) { const d = await r.json(); setPassages(d.passages || []); }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const passage = passages[passageIdx];
  const previewSentences = React.useMemo(() => (passage ? firstAndLastSentence(passage.text) : []), [passage]);

  const start = () => {
    setPreviewLeft(PREVIEW_SECS);
    setState('preview');
    previewTimer.current = setInterval(() => {
      setPreviewLeft((s) => {
        if (s <= 1) {
          clearInterval(previewTimer.current);
          startedAt.current = Date.now();
          setState('reading');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const stop = () => { durationRef.current = Date.now() - startedAt.current; setState('quiz'); };
  const reset = () => { setState('setup'); durationRef.current = 0; clearInterval(previewTimer.current); };

  if (!passage) {
    return (
      <main className="sharp-bg min-h-screen flex items-center justify-center text-white/60" data-testid="learn-preview-loading">
        Loading passages…
      </main>
    );
  }

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="learn-preview-page">
      <button onClick={() => navigate('/learn')} className="text-xs text-white/50 hover:text-white/80 mb-4 inline-flex items-center gap-1.5">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to LEARN
      </button>

      <header className="mb-6 flex items-center gap-3">
        <Eye className="w-6 h-6 text-indigo-300" />
        <div>
          <p className="eyebrow text-indigo-300 mb-0.5">Drill · Preview-then-read</p>
          <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">
            Build the map first<span className="text-indigo-300">.</span>
          </h1>
        </div>
      </header>

      {state === 'setup' && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Passage</p>
            <div className="relative">
              <select
                value={passageIdx}
                onChange={(e) => setPassageIdx(Number(e.target.value))}
                className="w-full appearance-none bg-black/40 border border-white/15 rounded-lg pl-3 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-400/60"
                data-testid="learn-preview-passage-select"
              >
                {passages.map((p, i) => (<option key={p.passage_id} value={i}>{p.title} — {p.author} ({p.word_count} words)</option>))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          </div>
          <p className="text-xs text-white/50 leading-relaxed">
            You&apos;ll see the opening and closing sentences for {PREVIEW_SECS} seconds — that&apos;s your mental
            map. Then the full passage appears and you read normally under a timer.
            Comprehension ≥ 80% earns bonus XP so the technique&apos;s payoff is visible.
          </p>
          <button
            onClick={start}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-preview-start"
          >
            <Play className="w-4 h-4" /> Start preview
          </button>
        </div>
      )}

      {state === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl bg-black/40 border border-indigo-500/30 px-4 py-2.5">
            <p className="text-[10px] uppercase tracking-widest text-indigo-300 font-black flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Preview
            </p>
            <p className="font-display text-3xl font-black text-white tabular-nums" data-testid="learn-preview-countdown">
              0:0{previewLeft}
            </p>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-indigo-500/25 p-6 space-y-4" data-testid="learn-preview-sentences">
            {previewSentences.map((s, i) => (
              <p key={i} className="text-white/90 leading-relaxed text-base sm:text-lg italic">
                {s}
              </p>
            ))}
          </div>
        </div>
      )}

      {state === 'reading' && (
        <div className="space-y-4">
          <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-indigo-500/20 p-6 text-white/90 leading-relaxed text-base sm:text-lg" data-testid="learn-preview-passage-text">
            {passage.text}
          </div>
          <button
            onClick={stop}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-preview-done"
          >
            <StopCircle className="w-4 h-4" /> Done — score me
          </button>
        </div>
      )}

      {state === 'quiz' && (
        <LearnQuiz passage={passage} drillKind="preview_read" durationMs={durationRef.current} onDone={() => setState('done')} />
      )}

      {state === 'done' && (
        <div className="mt-5 flex gap-3">
          <button onClick={reset} className="flex-1 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-3 text-sm font-black" data-testid="learn-preview-again">
            Run another drill
          </button>
          <button onClick={() => navigate('/learn')} className="rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-3 text-sm font-black">
            Back to LEARN
          </button>
        </div>
      )}
    </main>
  );
};

export default LearnPreviewRead;
